import { IImpersonationContext } from '@ViewModels';
import { StyleSheet, css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { IMetaAdMetricsSummary, IMetaCampaignMetricsSummary } from '../../../../extViewmodels/sdk/models/Ads';
import { useGetMetaAds } from '../../../../queries/Ads/useGetMetaAds';
import { useGetMetaAdsCampaignPerformance } from '../../../../queries/Ads/useGetMetaAdsCampaignPerformance';
import { useGetMetaAdsProvider } from '../../../../queries/Ads/useGetMetaAdsProvider';
import { Dialog, DialogBody, DialogHeader, DialogPopup, DialogTitle } from '../../../../web/components-v2/Dialog';
import { THEME } from '../../../../web/components-v2/Theme/theme';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';

const { colors, spacing, typography } = THEME;

interface IProps {
	impersonationContext: IImpersonationContext;
}

function formatCurrency(value: number): string {
	if (!isFinite(value)) return '—';
	return `$${value.toFixed(2)}`;
}

function formatPercent(value: number): string {
	if (!isFinite(value)) return '—';
	return `${value.toFixed(2)}%`;
}

export function AdsCampaignsTable({ impersonationContext }: IProps) {
	const { data: provider, isPending: isProviderPending } = useGetMetaAdsProvider({ impersonationContext });

	const adAccountId = provider?.adAccountIds?.[0] ?? '';
	const isConnected = provider?.enabled && !!adAccountId;

	// Memoized to prevent new Date objects on every render from causing infinite query loops
	const startDate = React.useMemo(() => moment().subtract(30, 'days').toDate(), []);
	const endDate = React.useMemo(() => new Date(), []);

	const { data: campaignData, isPending: isCampaignPending } = useGetMetaAdsCampaignPerformance({
		impersonationContext,
		startDate,
		endDate,
		enabled: isConnected,
	});

	const { data: adsData, isPending: isAdsPending } = useGetMetaAds({
		impersonationContext,
		adAccountId,
		enabled: isConnected,
	});

	const [selectedCampaign, setSelectedCampaign] = React.useState<IMetaCampaignMetricsSummary | null>(null);

	const adStatusMap = React.useMemo(() => {
		const map = new Map<string, string>();
		if (adsData) {
			for (const ad of adsData) {
				map.set(ad.id, ad.effectiveStatus);
			}
		}
		return map;
	}, [adsData]);

	const campaignStatusMap = React.useMemo(() => {
		const map = new Map<string, string>();
		if (adsData) {
			for (const ad of adsData) {
				const existing = map.get(ad.campaignId);
				// If any ad is ACTIVE, the campaign is ACTIVE; otherwise use the first status found
				if (!existing || ad.effectiveStatus === 'ACTIVE') {
					map.set(ad.campaignId, ad.effectiveStatus);
				}
			}
		}
		return map;
	}, [adsData]);

	if (isProviderPending || (isConnected && (isCampaignPending || isAdsPending))) {
		return (
			<div className={css(styles.centerContainer)}>
				<LoadingSpinner />
			</div>
		);
	}

	if (!isConnected) {
		return null;
	}

	const campaigns = campaignData ?? [];

	if (campaigns.length === 0) {
		return <p className={css(styles.emptyText)}>No campaign data found.</p>;
	}

	return (
		<CampaignsContent
			campaigns={campaigns}
			campaignStatusMap={campaignStatusMap}
			adStatusMap={adStatusMap}
			selectedCampaign={selectedCampaign}
			setSelectedCampaign={setSelectedCampaign}
		/>
	);
}

function CampaignsContent({
	campaigns,
	campaignStatusMap,
	adStatusMap,
	selectedCampaign,
	setSelectedCampaign,
}: {
	campaigns: IMetaCampaignMetricsSummary[];
	campaignStatusMap: Map<string, string>;
	adStatusMap: Map<string, string>;
	selectedCampaign: IMetaCampaignMetricsSummary | null;
	setSelectedCampaign: (campaign: IMetaCampaignMetricsSummary | null) => void;
}) {
	return (
		<>
			<h3 className={css(styles.subsectionTitle)}>
				Campaign Performance <span className={css(styles.dateRangeLabel)}>(Last 30 days)</span>
			</h3>
			<div className={css(styles.campaignList)}>
				<div className={css(styles.campaignHeaderRow)}>
					<div className={css(styles.campaignHeaderCell, styles.campaignHeaderName)}>Campaign</div>
					<div className={css(styles.campaignHeaderCell, styles.campaignHeaderStatus)}>Status</div>
					<div className={css(styles.campaignHeaderCell)}>Ads</div>
					<div className={css(styles.campaignHeaderCell)}>Leads</div>
					<div className={css(styles.campaignHeaderCell)}>Spend</div>
					<div className={css(styles.campaignHeaderCell)}>Cost/Lead</div>
					<div className={css(styles.campaignHeaderCell)}>Impressions</div>
					<div className={css(styles.campaignHeaderCell)}>Clicks</div>
					<div className={css(styles.campaignHeaderCell)}>CTR %</div>
				</div>
				{campaigns.map(campaign => (
					<CampaignRow
						key={campaign.campaignId}
						campaign={campaign}
						campaignStatusMap={campaignStatusMap}
						onClick={() => setSelectedCampaign(campaign)}
					/>
				))}
			</div>

			<CampaignDetailModal
				campaign={selectedCampaign}
				adStatusMap={adStatusMap}
				campaignStatusMap={campaignStatusMap}
				onClose={() => setSelectedCampaign(null)}
			/>
		</>
	);
}

function CampaignRow({
	campaign,
	campaignStatusMap,
	onClick,
}: {
	campaign: IMetaCampaignMetricsSummary;
	campaignStatusMap: Map<string, string>;
	onClick: () => void;
}) {
	const status = campaignStatusMap.get(campaign.campaignId);
	return (
		<button className={css(styles.campaignRow)} onClick={onClick} type='button'>
			<div className={css(styles.campaignRowName)}>
				<span className={css(styles.campaignName)}>{campaign.campaignName}</span>
				{campaign.campaignObjective && <span className={css(styles.objectiveTag)}>{campaign.campaignObjective}</span>}
			</div>
			<div className={css(styles.campaignRowStatusCell)}>{status ? <StatusBadge status={status} /> : null}</div>
			<div className={css(styles.campaignRowCell)}>{campaign.adCount}</div>
			<div className={css(styles.campaignRowCell)}>{campaign.leads}</div>
			<div className={css(styles.campaignRowCell)}>{formatCurrency(campaign.spend)}</div>
			<div className={css(styles.campaignRowCell)}>{formatCurrency(campaign.costPerLead)}</div>
			<div className={css(styles.campaignRowCell)}>{campaign.impressions.toLocaleString()}</div>
			<div className={css(styles.campaignRowCell)}>{campaign.clicks.toLocaleString()}</div>
			<div className={css(styles.campaignRowCell)}>{formatPercent(campaign.clickThroughRate)}</div>
		</button>
	);
}

function CampaignDetailModal({
	campaign,
	adStatusMap,
	campaignStatusMap,
	onClose,
}: {
	campaign: IMetaCampaignMetricsSummary | null;
	adStatusMap: Map<string, string>;
	campaignStatusMap: Map<string, string>;
	onClose: () => void;
}) {
	const status = campaign ? campaignStatusMap.get(campaign.campaignId) : undefined;
	return (
		<Dialog open={!!campaign} onOpenChange={open => !open && onClose()}>
			<DialogPopup styles={[styles.modalPopup]}>
				{campaign && (
					<>
						<DialogHeader>
							<DialogTitle>{campaign.campaignName}</DialogTitle>
							<div className={css(styles.modalSubheader)}>
								{status && <StatusBadge status={status} />}
								{campaign.campaignObjective && (
									<span className={css(styles.modalObjective)}>{campaign.campaignObjective}</span>
								)}
							</div>
						</DialogHeader>
						<DialogBody>
							{campaign.ads.length > 0 && (
								<>
									<h3 className={css(styles.modalAdsTitle)}>Ads ({campaign.ads.length})</h3>
									<div className={css(styles.modalAdsTable)}>
										<div className={css(styles.modalAdsHeaderRow)}>
											<div className={css(styles.modalAdsCellName)}>Ad Name</div>
											<div className={css(styles.modalAdsCellStatus)}>Status</div>
											<div className={css(styles.modalAdsCell)}>Leads</div>
											<div className={css(styles.modalAdsCell)}>Spend</div>
											<div className={css(styles.modalAdsCell)}>Cost/Lead</div>
											<div className={css(styles.modalAdsCell)}>Impressions</div>
											<div className={css(styles.modalAdsCell)}>Clicks</div>
											<div className={css(styles.modalAdsCell)}>CTR %</div>
										</div>
										{campaign.ads.map(ad => (
											<ModalAdRow key={ad.adExternalId} ad={ad} status={adStatusMap.get(ad.adExternalId)} />
										))}
									</div>
								</>
							)}
						</DialogBody>
					</>
				)}
			</DialogPopup>
		</Dialog>
	);
}

function ModalAdRow({ ad, status }: { ad: IMetaAdMetricsSummary; status?: string }) {
	return (
		<div className={css(styles.modalAdsRow)}>
			<div className={css(styles.modalAdsCellName)}>
				<span className={css(styles.modalAdName)}>{ad.adName}</span>
			</div>
			<div className={css(styles.modalAdsCellStatus)}>{status ? <StatusBadge status={status} /> : null}</div>
			<div className={css(styles.modalAdsCell)}>{ad.leads}</div>
			<div className={css(styles.modalAdsCell)}>{formatCurrency(ad.spend)}</div>
			<div className={css(styles.modalAdsCell)}>{formatCurrency(ad.costPerLead)}</div>
			<div className={css(styles.modalAdsCell)}>{ad.impressions.toLocaleString()}</div>
			<div className={css(styles.modalAdsCell)}>{ad.clicks.toLocaleString()}</div>
			<div className={css(styles.modalAdsCell)}>{formatPercent(ad.clickThroughRate)}</div>
		</div>
	);
}

const BAD_STATUSES = new Set(['DISAPPROVED', 'WITH_ISSUES', 'DELETED', 'ARCHIVED', 'ERROR']);

function StatusBadge({ status }: { status: string }) {
	const isActive = status === 'ACTIVE';
	const isPaused =
		status === 'PAUSED' || status === 'CAMPAIGN_PAUSED' || status === 'ADSET_PAUSED' || status === 'PENDING_REVIEW';
	const isBad = BAD_STATUSES.has(status);
	return (
		<span
			className={css(
				styles.statusBadge,
				isActive && styles.statusActive,
				isPaused && styles.statusPaused,
				isBad && styles.statusBad,
				!isActive && !isPaused && !isBad && styles.statusOther
			)}
		>
			{status}
		</span>
	);
}

const CAMPAIGN_GRID = '2fr 1fr 0.5fr 0.7fr 0.7fr 0.7fr 0.8fr 0.7fr 0.7fr';

const styles = StyleSheet.create({
	centerContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: `${spacing.xl}px 0`,
	},
	emptyText: {
		color: colors.text.light,
		fontSize: typography.fontSizes.md,
	},
	subsectionTitle: {
		fontSize: typography.fontSizes.md,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.main,
		margin: 0,
		marginBottom: spacing.sm,
		marginTop: spacing.md,
	},
	dateRangeLabel: {
		fontSize: typography.fontSizes.sm,
		fontWeight: typography.fontWeights.standard,
		color: colors.text.light,
	},

	// Compact campaign list
	campaignList: {
		border: `1px solid ${colors.border.light}`,
		borderRadius: 8,
		overflow: 'hidden',
	},
	campaignHeaderRow: {
		display: 'grid',
		gridTemplateColumns: CAMPAIGN_GRID,
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
		backgroundColor: colors.hover.light,
		borderBottom: `1px solid ${colors.border.light}`,
	},
	campaignHeaderCell: {
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.bold,
		color: colors.text.light,
		textTransform: 'uppercase' as const,
		textAlign: 'right' as const,
	},
	campaignHeaderName: {
		textAlign: 'left' as const,
	},
	campaignHeaderStatus: {
		textAlign: 'left' as const,
	},
	campaignRow: {
		display: 'grid',
		gridTemplateColumns: CAMPAIGN_GRID,
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
		alignItems: 'center',
		width: '100%',
		border: 'none',
		borderBottom: `1px solid ${colors.border.light}`,
		backgroundColor: 'transparent',
		cursor: 'pointer',
		textAlign: 'left' as const,
		':hover': {
			backgroundColor: colors.hover.light,
		},
		':last-child': {
			borderBottom: 'none',
		},
	},
	campaignRowName: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 2,
		minWidth: 0,
	},
	campaignName: {
		fontSize: typography.fontSizes.sm,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.main,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	objectiveTag: {
		fontSize: typography.fontSizes.xs,
		color: colors.primary.main,
		fontWeight: typography.fontWeights.semibold,
	},
	campaignRowStatusCell: {
		textAlign: 'left' as const,
	},
	campaignRowCell: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
		fontVariantNumeric: 'tabular-nums',
		textAlign: 'right' as const,
	},

	// Modal
	modalPopup: {
		maxWidth: 800,
		width: '90%',
		maxHeight: '80vh',
		overflowY: 'auto' as const,
	},
	modalSubheader: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
	},
	modalObjective: {
		fontSize: typography.fontSizes.sm,
		color: colors.primary.main,
		fontWeight: typography.fontWeights.semibold,
	},
	modalAdsTitle: {
		fontSize: typography.fontSizes.md,
		fontWeight: typography.fontWeights.semibold,
		color: colors.text.main,
		margin: 0,
		marginBottom: spacing.sm,
	},
	modalAdsTable: {
		border: `1px solid ${colors.border.light}`,
		borderRadius: 6,
		overflow: 'hidden',
	},
	modalAdsHeaderRow: {
		display: 'grid',
		gridTemplateColumns: '2fr 1fr repeat(6, 0.8fr)',
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
		backgroundColor: colors.hover.light,
		borderBottom: `1px solid ${colors.border.light}`,
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.bold,
		color: colors.text.light,
		textTransform: 'uppercase' as const,
	},
	modalAdsRow: {
		display: 'grid',
		gridTemplateColumns: '2fr 1fr repeat(6, 0.8fr)',
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
		alignItems: 'center',
		borderBottom: `1px solid ${colors.border.light}`,
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
		':last-child': {
			borderBottom: 'none',
		},
	},
	modalAdsCellName: {
		minWidth: 0,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	modalAdsCellStatus: {},
	modalAdsCell: {
		textAlign: 'right' as const,
		fontVariantNumeric: 'tabular-nums',
	},
	modalAdName: {
		fontSize: typography.fontSizes.sm,
		color: colors.text.main,
	},

	// Status badges
	statusBadge: {
		display: 'inline-block',
		padding: `2px ${spacing.sm}px`,
		borderRadius: 4,
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.semibold,
		textTransform: 'uppercase' as const,
		width: 'fit-content',
	},
	statusActive: {
		backgroundColor: colors.success.light,
		color: colors.success.dark,
	},
	statusPaused: {
		backgroundColor: colors.warning.light,
		color: colors.warning.dark,
	},
	statusBad: {
		backgroundColor: colors.danger.light,
		color: colors.danger.dark,
	},
	statusOther: {
		backgroundColor: colors.hover.light,
		color: colors.text.light,
	},
});
